import { toast } from "react-hot-toast";
import axiosInstance from "./apiinterseptor";

const getApiErrorMessage = (error: any, fallback: string) => {
  const message = error?.response?.data?.message || error?.message || fallback;
  if (String(message).toLowerCase().includes("duplicate field value")) {
    return "This Sheet Job ID is already assigned to an allocation.";
  }
  return message;
};

export const getMappedHunarSheetJobIds = async (jobId?: string) => {
  try {
    const response = await axiosInstance.get("/hunar-allocations/mapped-job-ids", {
      params: jobId ? { jobId } : undefined,
    });
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to fetch mapped job IDs"));
    throw error;
  }
};

export const getHunarAllocations = async () => {
  try {
    const response = await axiosInstance.get("/hunar-allocations");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to fetch Hunar allocations"));
    throw error;
  }
};

export const createHunarAllocation = async (payload: {
  internalJobId: string;
  hunarAgentId: string;
  assignedRecruiterId: string;
  status?: "active" | "inactive";
  dailyCap?: number;
}) => {
  try {
    const response = await axiosInstance.post("/hunar-allocations", payload);
    toast.success("Hunar allocation created");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to create Hunar allocation"));
    throw error;
  }
};

export const updateHunarAllocation = async (
  allocationId: string,
  payload: {
    internalJobId?: string;
    hunarAgentId?: string;
    assignedRecruiterId?: string;
    status?: "active" | "inactive";
    dailyCap?: number;
  }
) => {
  try {
    const response = await axiosInstance.patch(`/hunar-allocations/${allocationId}`, payload);
    toast.success("Hunar allocation updated");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to update Hunar allocation"));
    throw error;
  }
};

export const addHunarSheetJobIdFromDashboardJob = async (jobId: string, sheetJobId?: string) => {
  try {
    const response = await axiosInstance.post(`/hunar-allocations/jobs/${jobId}/sheet-job-mapping`, {
      ...(sheetJobId ? { sheetJobId } : {}),
    });
    toast.success("Job ID added for AI Funnel");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to add job ID"));
    throw error;
  }
};

export const createHunarSheetJobMapping = async (allocationId: string, sheetJobId: string) => {
  try {
    const response = await axiosInstance.post(`/hunar-allocations/${allocationId}/sheet-job-mappings`, {
      sheetJobId,
    });
    toast.success("Sheet job ID added");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to add sheet job ID"));
    throw error;
  }
};

export const updateHunarSheetJobMapping = async (
  mappingId: string,
  status: "active" | "inactive"
) => {
  try {
    const response = await axiosInstance.patch(`/hunar-allocations/sheet-job-mappings/${mappingId}`, {
      status,
    });
    toast.success("Sheet job mapping updated");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to update sheet job mapping"));
    throw error;
  }
};

export const deleteHunarSheetJobMapping = async (mappingId: string) => {
  try {
    const response = await axiosInstance.delete(`/hunar-allocations/sheet-job-mappings/${mappingId}`);
    toast.success("Sheet job ID removed");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to remove sheet job ID"));
    throw error;
  }
};

export const getHunarInputSheets = async () => {
  try {
    const response = await axiosInstance.get("/hunar-allocations/input-sheets");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to fetch input sheets"));
    throw error;
  }
};

export const getHunarSheetIngestTracking = async () => {
  try {
    const response = await axiosInstance.get("/hunar-allocations/sheet-ingest-tracking");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to fetch sheet ingest tracking"));
    throw error;
  }
};

export const createHunarInputSheet = async (payload: {
  spreadsheetUrl: string;
  sheetName: string;
  label?: string;
  columnMap?: {
    candidateName?: string;
    mobileNumber?: string;
    jobId?: string;
    email?: string;
  };
}) => {
  try {
    const response = await axiosInstance.post("/hunar-allocations/input-sheets", payload);
    toast.success("Input sheet added");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to add input sheet"));
    throw error;
  }
};

export const updateHunarInputSheet = async (
  sheetId: string,
  payload: {
    status?: "active" | "inactive";
    sheetName?: string;
    label?: string;
    spreadsheetUrl?: string;
    columnMap?: {
      candidateName?: string;
      mobileNumber?: string;
      jobId?: string;
      email?: string;
    };
  }
) => {
  try {
    const response = await axiosInstance.patch(`/hunar-allocations/input-sheets/${sheetId}`, payload);
    toast.success("Input sheet updated");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to update input sheet"));
    throw error;
  }
};

export const deleteHunarInputSheet = async (sheetId: string) => {
  try {
    const response = await axiosInstance.delete(`/hunar-allocations/input-sheets/${sheetId}`);
    toast.success("Input sheet deleted");
    return response.data;
  } catch (error: any) {
    toast.error(getApiErrorMessage(error, "Failed to delete input sheet"));
    throw error;
  }
};
